import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import { showToast } from './toast'

export interface AdminAccountDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

// Import auth service (will be dynamically imported to avoid build issues)
let authService: any = null
const getAuthService = async () => {
  if (!authService) {
    try {
      const module = await import('@joinomu/shared')
      authService = module.authService
    } catch (error) {
      console.warn('Auth service not available:', error)
      // Return mock service for fallback
      authService = {
        getCurrentUser: () => ({ user: null, error: null }),
        getUserProfile: () => ({ data: null, error: null }),
        updateAdminProfile: () => ({ success: true, error: null }),
        updatePassword: () => ({ success: true, error: null })
      }
    }
  }
  return authService
}

export function AdminAccountDialog({
  open = false,
  onOpenChange
}: AdminAccountDialogProps) {
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [hasChanges, setHasChanges] = React.useState(false)

  // Form data state
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  })

  // Original data for change tracking
  const [originalData, setOriginalData] = React.useState({
    firstName: '',
    lastName: '',
    email: ''
  })

  // Load user profile data when dialog opens
  React.useEffect(() => {
    if (open) {
      loadUserProfile()
    }
  }, [open])

  // Track changes
  React.useEffect(() => {
    const hasFormChanges = formData.firstName !== originalData.firstName ||
                          formData.lastName !== originalData.lastName ||
                          formData.email !== originalData.email ||
                          formData.password !== ''
    setHasChanges(hasFormChanges)
  }, [formData, originalData])

  const loadUserProfile = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const auth = await getAuthService()
      const { data, error } = await auth.getUserProfile()
      
      if (error) {
        setError('Failed to load profile data')
        console.error('Admin account dialog profile load error:', error)
      } else if (data) {
        console.log('Admin profile data loaded:', data)
        const profileData = {
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: data.email || ''
        }
        
        setFormData({
          ...profileData,
          password: ''
        })
        setOriginalData(profileData)
      }
    } catch (error) {
      console.error('Error loading admin profile:', error)
      setError('Failed to load profile data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      
      const auth = await getAuthService()
      
      // Update profile if basic info changed
      if (formData.firstName !== originalData.firstName ||
          formData.lastName !== originalData.lastName ||
          formData.email !== originalData.email) {
        
        const profileUpdate = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email
        }
        
        console.log('Updating admin profile:', profileUpdate)
        const { success, error: profileError } = await auth.updateAdminProfile(profileUpdate)
        
        if (!success) {
          throw new Error(profileError?.message || 'Failed to update profile')
        }
      }
      
      // Update password if provided
      if (formData.password) {
        console.log('Updating password')
        const { success, error: passwordError } = await auth.updatePassword(formData.password)
        
        if (!success) {
          throw new Error(passwordError?.message || 'Failed to update password')
        }
      }
      
      // Update original data to reflect changes
      setOriginalData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      })
      
      // Clear password field
      setFormData(prev => ({ ...prev, password: '' }))
      
      showToast({
        title: "Success",
        description: "Account updated successfully",
        variant: "success"
      })
      
      // Close dialog
      onOpenChange?.(false)
      
    } catch (error: any) {
      console.error('Error saving admin account:', error)
      setError(error.message || 'Failed to save changes')
      showToast({
        title: "Error", 
        description: error.message || "Failed to save changes",
        variant: "error"
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    // Reset form to original data
    setFormData({
      ...originalData,
      password: ''
    })
    setError(null)
    onOpenChange?.(false)
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
          
          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-sm text-muted-foreground">Loading profile...</div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">New Password (optional)</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Leave blank to keep current password"
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isLoading}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}